import {
  Add,
  AssignmentTurnedIn,
  CalendarMonth,
  CalendarToday,
  CheckCircle,
  CheckCircleOutline,
  Delete,
  Edit,
  Event,
  EventAvailable,
  FormatListBulleted,
  MarkEmailRead,
  OpenInNew,
  People,
  Replay,
  Schedule,
  Search,
  ViewWeek,
} from '@mui/icons-material';
import {
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { usePermissions } from 'hooks/auth/usePermissions';
import { useLeads } from 'hooks/admin/useLeads';
import { useContacts } from 'hooks/contacts/useContacts';
import { useTasks } from 'hooks/tasks';
import { buildTaskAssignableUsers, useInventoryAssignableUsers, useSalesAssignableUsers } from 'hooks/useUsers';
import {
  Task,
  TaskAutomationLevel,
  TaskEvent,
  TaskStatus,
  TASK_EVENT_LABELS,
} from '@otw/models/tasks';
import type { Contact } from '@otw/models/leads/contacts';
import { MobileRecordAction, MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import type { StatusTone } from 'core/theme.tokens'
import { TOUCH_MIN } from 'core/theme.tokens'
import { TaskCard } from '../cards';
import ContactPreviewPane from '../ContactPreviewPane';
import {
  CompleteTaskCommunicationDialog,
  ContactFormDialog,
  isSupportedCommunicationTaskEvent,
  TaskFormDialog,
  ViewSentCommunicationDialog,
} from '../dialogs';
import type { SentCommunicationResult, TaskFormSubmitPayload, TaskAssignableUser } from '../dialogs';
import { CrmPageActions } from '../CrmPageChrome';
import { buildTaskCustomerOptionsFromContacts } from '../../util/contactLeadSync';
import { openContactDetailInNewTab } from '../../util/contactRoutes';
import { logTaskCreationContactActivity } from '../../util/contactTaskActivity';
import type { TaskCustomerOption } from '../../util/taskUtils';
import { mailTemplates, sendTemplatedEmail } from '../../util/mail';
import { sendTemplatedSms, smsTemplates } from '../../util/sms';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type TaskRange = 'list' | 'day' | 'week' | 'month';

const isTaskOverdue = (task: Task): boolean =>
  task.status !== 'completed' &&
  task.status !== 'cancelled' &&
  dayjs(task.dueDate).isBefore(dayjs().startOf('day'));

interface TaskRow {
  id: string;
  task: Task;
  title: string;
  contactId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  event: TaskEvent;
  dueDate: Date | null;
  status: TaskStatus;
  automationLevel: TaskAutomationLevel;
}

const taskStatusDisplay = (row: TaskRow): { tone: StatusTone; label: string } => {
  if (row.status === 'completed') return { tone: 'success', label: 'Completed' };
  if (row.status === 'cancelled') return { tone: 'neutral', label: 'Cancelled' };
  if (isTaskOverdue(row.task)) return { tone: 'danger', label: 'Overdue' };
  return { tone: 'waiting', label: 'Pending' };
};

interface TasksTabProps {
  isActive: boolean;
}

const TasksTab: React.FC<TasksTabProps> = ({ isActive }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { userInfo } = useAuth();
  const { leadManagementScope } = usePermissions();
  const { leads, isLoading: isLeadsLoading } = useLeads();
  const { contacts, logContactActivity } = useContacts();
  const { users: salesAssignableUsers } = useSalesAssignableUsers();
  const { users: inventoryAssignableUsers } = useInventoryAssignableUsers();
  const {
    tasks,
    isLoading: isTasksLoading,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,
  } = useTasks();

  const [range, setRange] = useState<TaskRange>('list');
  const [anchorDate, setAnchorDate] = useState<Dayjs>(dayjs().startOf('day'));
  const [contactFilterId, setContactFilterId] = useState<string>('');
  const [listSearch, setListSearch] = useState('');
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showOtherSalesReps, setShowOtherSalesReps] = useState(false);
  const [salesRepFilterId, setSalesRepFilterId] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [communicationTask, setCommunicationTask] = useState<Task | null>(null);
  const [viewingCommunicationTask, setViewingCommunicationTask] = useState<Task | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const contactsById = useMemo(() => {
    const map = new Map<string, Contact>();
    contacts.forEach((c) => map.set(c.id, c));
    return map;
  }, [contacts]);

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === selectedContactId) ?? null,
    [contacts, selectedContactId],
  );

  const hasFullAccess = leadManagementScope === 'all';

  const allContactCustomers = useMemo(
    () => buildTaskCustomerOptionsFromContacts(contacts, leads ?? []),
    [contacts, leads],
  );

  // Reps without full access: contacts they own or that have a lead assigned to them.
  const myContactCustomers: TaskCustomerOption[] = useMemo(() => {
    if (!userInfo?.id) return [];
    const userId = String(userInfo.id);
    return allContactCustomers.filter((option) => {
      const contact = contacts.find((c) => c.id === option.id);
      if (!contact) return false;
      if (contact.ownerId && String(contact.ownerId) === userId) return true;
      if (contact.inventoryOwnerId && String(contact.inventoryOwnerId) === userId) return true;
      if (!leads?.length) return false;
      return option.leads.some((summary) => {
        const lead = leads.find((l) => l.id === summary.id);
        return lead && String(lead.assignedRepId) === userId;
      });
    });
  }, [allContactCustomers, contacts, leads, userInfo?.id]);

  const filterContacts: TaskCustomerOption[] = hasFullAccess
    ? allContactCustomers
    : myContactCustomers;

  const formContacts: TaskCustomerOption[] = filterContacts;

  // Reps that a full-access user can assign tasks to: sales, inventory, and owners.
  const assignableUsers: TaskAssignableUser[] = useMemo(
    () => buildTaskAssignableUsers(hasFullAccess, salesAssignableUsers, inventoryAssignableUsers),
    [hasFullAccess, salesAssignableUsers, inventoryAssignableUsers],
  );

  const myTasks = useMemo(() => {
    if (!tasks) return [];
    if (leadManagementScope === 'none') return [];
    if (leadManagementScope === 'all' && showOtherSalesReps) return tasks;
    if (!userInfo?.id) return [];
    return tasks.filter((task) => String(task.assignedToId) === String(userInfo.id));
  }, [tasks, userInfo?.id, leadManagementScope, showOtherSalesReps]);

  const tasksAfterRepFilter = useMemo(() => {
    if (!salesRepFilterId) return myTasks;
    return myTasks.filter((task) => String(task.assignedToId) === salesRepFilterId);
  }, [myTasks, salesRepFilterId]);

  const filterContactsWithTasks = useMemo(() => {
    const contactIdsWithTasks = new Set(
      tasksAfterRepFilter.map((task) => task.contact_id).filter((id): id is string => Boolean(id)),
    );
    return filterContacts
      .filter((contact) => contactIdsWithTasks.has(contact.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [filterContacts, tasksAfterRepFilter]);

  const selectedFilterContact = useMemo(
    () => filterContactsWithTasks.find((contact) => contact.id === contactFilterId) ?? null,
    [filterContactsWithTasks, contactFilterId],
  );

  const tasksForContact = useMemo(() => {
    if (!contactFilterId) return tasksAfterRepFilter;
    return tasksAfterRepFilter.filter((task) => task.contact_id === contactFilterId);
  }, [tasksAfterRepFilter, contactFilterId]);

  const { rangeStart, rangeEnd, rangeLabel, weekDays, monthGridDays } = useMemo(() => {
    const emptyMonthDays = [] as Dayjs[];
    if (range === 'day') {
      const start = anchorDate.startOf('day');
      const end = anchorDate.endOf('day');
      return {
        rangeStart: start,
        rangeEnd: end,
        rangeLabel: anchorDate.format('dddd, MMM D, YYYY'),
        weekDays: [] as Dayjs[],
        monthGridDays: emptyMonthDays,
      };
    }
    if (range === 'month') {
      const monthStart = anchorDate.startOf('month');
      const monthEnd = anchorDate.endOf('month');
      const start = monthStart.startOf('day');
      const end = monthEnd.endOf('day');
      const rangeLabelMonth = anchorDate.format('MMMM YYYY');
      // Monday-first grid (same convention as the week view’s Monday column).
      const daysFromMonday = (monthStart.day() + 6) % 7;
      const gridStart = monthStart.subtract(daysFromMonday, 'day');
      const days: Dayjs[] = [];
      let cursor = gridStart;
      while (cursor.isBefore(monthEnd, 'day') || cursor.isSame(monthEnd, 'day')) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      while (days.length % 7 !== 0) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      return {
        rangeStart: start,
        rangeEnd: end,
        rangeLabel: rangeLabelMonth,
        weekDays: [] as Dayjs[],
        monthGridDays: days,
      };
    }
    const monday = anchorDate.startOf('week').add(1, 'day');
    const days = Array.from({ length: 5 }, (_, i) => monday.add(i, 'day'));
    const start = days[0].startOf('day');
    const end = days[days.length - 1].endOf('day');
    return {
      rangeStart: start,
      rangeEnd: end,
      rangeLabel: `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`,
      weekDays: days,
      monthGridDays: emptyMonthDays,
    };
  }, [range, anchorDate]);

  const overdueTasks = useMemo(
    () =>
      tasksForContact
        .filter(isTaskOverdue)
        .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf()),
    [tasksForContact],
  );

  const listRows = useMemo<TaskRow[]>(() => {
    const query = listSearch.trim().toLowerCase();
    return tasksForContact
      .map((task) => {
        const contact = contactsById.get(task.contact_id);
        return {
          id: task.id,
          task,
          title: task.title,
          contactId: task.contact_id ?? '',
          contactName: contact?.name || 'Unknown contact',
          contactEmail: contact?.email ?? '',
          contactPhone: contact?.phone ?? '',
          event: task.event,
          dueDate: task.dueDate ? new Date(task.dueDate) : null,
          status: task.status,
          automationLevel: task.automationLevel ?? 'partial',
        };
      })
      .filter((row) => {
        if (!showCompletedTasks && row.status === 'completed') return false;
        if (!query) return true;
        return (
          row.title.toLowerCase().includes(query) ||
          row.contactName.toLowerCase().includes(query) ||
          row.contactEmail.toLowerCase().includes(query) ||
          row.contactPhone.toLowerCase().includes(query) ||
          TASK_EVENT_LABELS[row.event].toLowerCase().includes(query) ||
          (hasFullAccess &&
            row.task.assignedToName?.toLowerCase().includes(query))
        );
      });
  }, [tasksForContact, contactsById, listSearch, showCompletedTasks, hasFullAccess]);

  const visibleTasks = useMemo(() => {
    return tasksForContact
      .filter((task) => {
        const due = dayjs(task.dueDate);
        if (!due.isValid()) return false;
        return due.isSameOrAfter(rangeStart) && due.isSameOrBefore(rangeEnd);
      })
      .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf());
  }, [tasksForContact, rangeStart, rangeEnd]);

  const tasksByDayKey = useMemo(() => {
    const map = new Map<string, Task[]>();
    visibleTasks.forEach((task) => {
      const key = dayjs(task.dueDate).format('YYYY-MM-DD');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    });
    return map;
  }, [visibleTasks]);

  const groupedTasks = useMemo(
    () => [...tasksByDayKey.entries()].sort(([a], [b]) => a.localeCompare(b)),
    [tasksByDayKey],
  );

  const stats = useMemo(() => {
    const scopeTasks = range === 'list' ? tasksForContact : visibleTasks;
    const totalForRange = scopeTasks.length;
    const overdueAll = overdueTasks.length;
    const completedThisRange = scopeTasks.filter((t) => t.status === 'completed').length;
    const upcomingAll = tasksForContact.filter(
      (task) =>
        task.status === 'pending' && dayjs(task.dueDate).isAfter(dayjs().endOf('day')),
    ).length;
    return { totalForRange, overdueAll, completedThisRange, upcomingAll };
  }, [visibleTasks, tasksForContact, overdueTasks, range]);

  useEffect(() => {
    if (contactFilterId && !filterContactsWithTasks.some((c) => c.id === contactFilterId)) {
      setContactFilterId('');
    }
  }, [contactFilterId, filterContactsWithTasks]);

  useEffect(() => {
    if (!showOtherSalesReps && salesRepFilterId && salesRepFilterId !== String(userInfo?.id)) {
      setSalesRepFilterId('');
    }
  }, [showOtherSalesReps, salesRepFilterId, userInfo?.id]);

  const handleOpenContact = (task: Task) => {
    if (task.contact_id) {
      openContactDetailInNewTab(task.contact_id);
    }
  };

  if (!isActive) return null;

  const handleSubmit = async (task: TaskFormSubmitPayload) => {
    try {
      setIsCreating(true);
      if (editingTask) {
        await updateTask({ id: editingTask.id, updates: task });
      } else {
        const created = await createTask(task);
        await logTaskCreationContactActivity(task, created.id, logContactActivity);
      }
      setIsDialogOpen(false);
      setEditingTask(null);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingTask(null);
    setIsDialogOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTask(null);
  };

  const handleToggleComplete = async (task: Task) => {
    // Manual tasks are completed without opening the message dialog —
    // they represent work that can't be done by sending a single email/text.
    const isManual = task.automationLevel === 'manual';
    if (
      task.status !== 'completed' &&
      !isManual &&
      isSupportedCommunicationTaskEvent(task.event)
    ) {
      setCommunicationTask(task);
      return;
    }
    await toggleTaskComplete(task);
  };

  const handleSendTaskEmail = async ({
    to,
    customerName,
    personalMessage,
    senderName,
    templateName,
  }: {
    to: string;
    customerName: string;
    personalMessage: string;
    senderName: string | null;
    templateName: keyof typeof mailTemplates;
  }) => {
    return sendTemplatedEmail(to, templateName, {
      customerName,
      personalMessage,
      senderName: senderName || undefined,
    });
  };

  const handleSendTaskText = async ({
    to,
    customerName,
    personalMessage,
    senderName,
    templateName,
  }: {
    to: string;
    customerName: string;
    personalMessage: string;
    senderName: string | null;
    templateName: keyof typeof smsTemplates;
  }) => {
    return sendTemplatedSms(
      to,
      templateName,
      {
        customerName,
        personalMessage,
        senderName: senderName || undefined,
      },
      {
        sentBy: userInfo?.email || userInfo?.id || null,
        type: 'task_completion_text',
      },
    );
  };

  const handleCommunicationSent = async (task: Task, result: SentCommunicationResult) => {
    // Persist the artifacts on the task and mark complete in a single update so
    // we don't end up with a brief intermediate state.
    await updateTask({
      id: task.id,
      updates: {
        status: 'completed',
        completedAt: new Date(),
        completionEmail: result.email ?? task.completionEmail ?? null,
        completionText: result.text ?? task.completionText ?? null,
      },
    });
    setCommunicationTask(null);
  };

  const handleViewCommunication = (task: Task) => {
    setViewingCommunicationTask(task);
  };

  const isLoading = isLeadsLoading || isTasksLoading;

  const kpi = (icon: React.ReactNode, label: string, value: React.ReactNode, accent: string) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.02)} 100%)`,
        borderColor: alpha(accent, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(accent, 0.18),
            color: accent,
            width: 32,
            height: 32,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}>
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: '1rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const taskColumns: GridColDef<TaskRow>[] = [
    {
      field: 'dueDate',
      headerName: 'Due',
      flex: 0.85,
      minWidth: 170,
      sortComparator: (a: Date | null, b: Date | null) =>
        (a ? a.getTime() : Infinity) - (b ? b.getTime() : Infinity),
      renderCell: (params) => {
        const overdue = isTaskOverdue(params.row.task);
        const due = params.row.dueDate ? dayjs(params.row.dueDate) : null;
        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <CalendarMonth
              fontSize="small"
              sx={{ color: overdue ? 'error.main' : 'text.secondary' }}
            />
            <Typography
              variant="body2"
              fontWeight={overdue ? 700 : 500}
              color={overdue ? 'error.main' : 'text.primary'}
              noWrap
            >
              {due && due.isValid() ? due.format('ddd, MMM D, YYYY') : 'No due date'}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Task',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{
              textDecoration:
                params.row.status === 'completed' ? 'line-through' : 'none',
            }}
          >
            {params.row.title}
          </Typography>
          {params.row.task.notes ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.task.notes}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      flex: 1.1,
      minWidth: 250,
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, width: '100%' }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.16),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            {params.row.contactName?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.row.contactName}
            </Typography>
            {params.row.contactEmail ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block' }}
              >
                {params.row.contactEmail}
              </Typography>
            ) : null}
          </Box>
          {params.row.contactId ? (
            <Button
              className="task-contact-preview-action"
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedContactId(params.row.contactId);
              }}
              sx={{
                flexShrink: 0,
                py: 0.5,
                px: 1,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.4,
                borderRadius: 2,
              }}
            >
              Preview
            </Button>
          ) : null}
        </Stack>
      ),
    },
    {
      field: 'event',
      headerName: 'Type',
      flex: 0.7,
      minWidth: 160,
      valueGetter: (_value, row) => TASK_EVENT_LABELS[row.event],
      renderCell: (params) => (
        <Chip
          size="small"
          icon={<Event fontSize="inherit" />}
          label={TASK_EVENT_LABELS[params.row.event]}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 130,
      renderCell: (params) => {
        const { status, task } = params.row;
        if (status === 'completed') {
          return <Chip size="small" color="success" label="Completed" sx={{ fontWeight: 600 }} />;
        }
        if (status === 'cancelled') {
          return <Chip size="small" label="Cancelled" sx={{ fontWeight: 600 }} />;
        }
        if (isTaskOverdue(task)) {
          return <Chip size="small" color="error" label="Overdue" sx={{ fontWeight: 600 }} />;
        }
        return (
          <Chip size="small" color="default" variant="outlined" label="Pending" sx={{ fontWeight: 600 }} />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      flex: 0.9,
      minWidth: 190,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const { task } = params.row;
        const isCompleted = task.status === 'completed';
        const hasSent = Boolean(task.completionEmail || task.completionText);
        return (
          <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ width: '100%' }}>
            {isCompleted && hasSent ? (
              <Tooltip title="View sent email/text">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleViewCommunication(task);
                  }}
                >
                  <MarkEmailRead fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {task.contact_id ? (
              <Tooltip title="Open contact">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenContact(task);
                  }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={isCompleted ? 'Mark as pending' : 'Mark as complete'}>
              <IconButton
                size="small"
                color={isCompleted ? 'default' : 'success'}
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleComplete(task);
                }}
              >
                {isCompleted ? (
                  <Replay fontSize="small" />
                ) : (
                  <CheckCircleOutline fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit task">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditTask(task);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete task">
              <IconButton
                size="small"
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  void deleteTask(task.id);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ pb: 2 }}>
      <CrmPageActions active={isActive}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          disabled={formContacts.length === 0}
        >
          New task
        </Button>
      </CrmPageActions>

      {/* KPIs */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          {kpi(
            <CalendarToday />,
            range === 'list'
              ? 'All tasks'
              : range === 'day'
                ? "Today's tasks"
                : range === 'week'
                  ? "This week's tasks"
                  : "This month's tasks",
            stats.totalForRange,
            theme.palette.primary.main,
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(<Schedule />, 'Overdue', stats.overdueAll, theme.palette.error.main)}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(
            <AssignmentTurnedIn />,
            'Completed in range',
            stats.completedThisRange,
            theme.palette.success.main,
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(<EventAvailable />, 'Upcoming', stats.upcomingAll, theme.palette.info.main)}
        </Grid>
      </Grid>

      {/* Range + filters */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1,
          alignItems: { md: 'center' },
        }}
      >
        <Tabs
          value={range}
          onChange={(_, value: TaskRange) => setRange(value)}
          sx={{ minHeight: isMobile ? TOUCH_MIN : 36 }}
        >
          <Tab
            icon={<FormatListBulleted fontSize="small" />}
            iconPosition="start"
            value="list"
            label="List"
            sx={{ minHeight: isMobile ? TOUCH_MIN : 36 }}
          />
          <Tab
            icon={<CalendarToday fontSize="small" />}
            iconPosition="start"
            value="day"
            label="Day"
            sx={{ minHeight: isMobile ? TOUCH_MIN : 36 }}
          />
          <Tab
            icon={<ViewWeek fontSize="small" />}
            iconPosition="start"
            value="week"
            label="Week"
            sx={{ minHeight: isMobile ? TOUCH_MIN : 36 }}
          />
          <Tab
            icon={<CalendarMonth fontSize="small" />}
            iconPosition="start"
            value="month"
            label="Month"
            sx={{ minHeight: isMobile ? TOUCH_MIN : 36 }}
          />
        </Tabs>

        {range !== 'list' && (
          <>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" onClick={() => setAnchorDate((d) => d.subtract(1, range))}>
                ‹ Prev
              </Button>
              <Button
                size="small"
                variant={
                  (range === 'day' && anchorDate.isSame(dayjs(), 'day')) ||
                  (range === 'month' && anchorDate.isSame(dayjs(), 'month'))
                    ? 'contained'
                    : 'outlined'
                }
                onClick={() => setAnchorDate(dayjs().startOf('day'))}
              >
                Today
              </Button>
              <Button size="small" onClick={() => setAnchorDate((d) => d.add(1, range))}>
                Next ›
              </Button>
            </Stack>

            <Chip
              size="small"
              label={rangeLabel}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.dark,
              }}
            />
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {hasFullAccess && (
          <Tooltip title="Include tasks assigned to other sales and inventory reps">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showOtherSalesReps}
                  onChange={(event) => setShowOtherSalesReps(event.target.checked)}
                  icon={<People fontSize="small" />}
                  checkedIcon={<People fontSize="small" />}
                />
              }
              label="Show other reps"
              sx={{ whiteSpace: 'nowrap', mr: 0 }}
            />
          </Tooltip>
        )}

        {range === 'list' && (
          <>
           <Tooltip title="Include tasks marked as completed">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showCompletedTasks}
                    onChange={(event) => setShowCompletedTasks(event.target.checked)}
                    color="success"
                    icon={<CheckCircleOutline fontSize="small" sx={{ color: 'action.disabled' }} />}
                    checkedIcon={<CheckCircle fontSize="small" sx={{ color: 'success.main' }} />}
                  />
                }
                label="Show completed"
                sx={{ whiteSpace: 'nowrap', mr: 0 }}
              />
            </Tooltip>
            <TextField
              size="small"
              placeholder="Search tasks"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', md: 240 } }}
            />
           
          </>
        )}

        {hasFullAccess && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 } }}>
            <InputLabel>Rep</InputLabel>
            <Select
              value={salesRepFilterId}
              onChange={(e) => {
                const value = e.target.value;
                setSalesRepFilterId(value);
                if (value && value !== String(userInfo?.id)) {
                  setShowOtherSalesReps(true);
                }
              }}
              label="Rep"
            >
              <MenuItem value="">All reps</MenuItem>
              {assignableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Autocomplete
          size="small"
          options={filterContactsWithTasks}
          value={selectedFilterContact}
          onChange={(_, value) => setContactFilterId(value?.id ?? '')}
          getOptionLabel={(option) => option.displayName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {option.displayName}
                </Typography>
                {option.email ? (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.email}
                  </Typography>
                ) : null}
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contact"
              placeholder={hasFullAccess ? 'All contacts' : 'All my contacts'}
            />
          )}
          sx={{ minWidth: { xs: '100%', md: 280 } }}
        />
      </Paper>

      {/* Empty/no-customers helpers — full-access users see all customers, so
          we only nudge sales reps who haven't been assigned any leads yet. */}
      {!hasFullAccess && myContactCustomers.length === 0 && !isLeadsLoading && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 3,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderColor: alpha(theme.palette.warning.main, 0.25),
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            No customers from your leads yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can only create tasks for customers tied to leads where you’re the sales rep. Get assigned to a
            lead, then come back to plan tasks.
          </Typography>
        </Paper>
      )}

      {/* Task list */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
          <CircularProgress />
        </Box>
      ) : range === 'list' ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {isMobile ? (
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <MobileRecordList isEmpty={listRows.length === 0} empty="No tasks yet.">
                {listRows.map((row) => {
                  const isCompleted = row.task.status === 'completed';
                  const hasSent = Boolean(row.task.completionEmail || row.task.completionText);
                  return (
                    <MobileRecordCard
                      key={row.id}
                      title={row.title}
                      meta={row.contactName || 'No contact'}
                      detail={
                        row.dueDate
                          ? dayjs(row.dueDate).format('ddd, MMM D, YYYY')
                          : undefined
                      }
                      status={taskStatusDisplay(row)}
                      chips={[{ label: TASK_EVENT_LABELS[row.event], tone: 'info' }]}
                      trailing={
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          {isCompleted && hasSent ? (
                            <MobileRecordAction
                              label="View sent email or text"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleViewCommunication(row.task);
                              }}
                            >
                              <MarkEmailRead fontSize="small" />
                            </MobileRecordAction>
                          ) : null}
                          {row.task.contact_id ? (
                            <MobileRecordAction
                              label="Open contact"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenContact(row.task);
                              }}
                            >
                              <OpenInNew fontSize="small" />
                            </MobileRecordAction>
                          ) : null}
                          <MobileRecordAction
                            label={isCompleted ? 'Mark as pending' : 'Mark as complete'}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleComplete(row.task);
                            }}
                          >
                            {isCompleted ? (
                              <Replay fontSize="small" />
                            ) : (
                              <CheckCircleOutline fontSize="small" />
                            )}
                          </MobileRecordAction>
                        </Stack>
                      }
                      onClick={() => handleEditTask(row.task)}
                    />
                  );
                })}
              </MobileRecordList>
            </Box>
          ) : (
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <DataGrid<TaskRow>
              rows={listRows}
              columns={taskColumns}
              autoHeight
              density="compact"
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: 'dueDate', sort: 'asc' }] },
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              getRowHeight={() => 64}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 700,
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row .task-contact-preview-action': {
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.15s ease-in-out',
                },
                '& .MuiDataGrid-row:hover .task-contact-preview-action, & .MuiDataGrid-row:focus-within .task-contact-preview-action':
                  {
                    opacity: 1,
                    pointerEvents: 'auto',
                  },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                  {
                    outline: 'none',
                  },
              }}
              slots={{
                noRowsOverlay: () => {
                  const hasHiddenCompleted =
                    !showCompletedTasks &&
                    tasksForContact.some((task) => task.status === 'completed');
                  return (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{ height: '100%', p: 4 }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {listSearch.trim()
                          ? 'No matches'
                          : hasHiddenCompleted
                            ? 'No open tasks'
                            : 'No tasks yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {listSearch.trim()
                          ? 'Try a different search or contact filter.'
                          : hasHiddenCompleted
                            ? 'Completed tasks are hidden. Turn on "Show completed" to view them.'
                            : 'Create a task to get started.'}
                      </Typography>
                    </Stack>
                  );
                },
              }}
            />
          </Paper>
          )}
          {selectedContact ? (
            <ContactPreviewPane
              contact={selectedContact}
              onClose={() => setSelectedContactId(null)}
              onEdit={(c) => {
                setEditingContact(c);
                setContactFormOpen(true);
              }}
            />
          ) : null}
        </Box>
      ) : range === 'week' ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            alignItems: 'start',
          }}
        >
          {weekDays.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const dayTasks = tasksByDayKey.get(key) || [];
            const isToday = day.isSame(dayjs(), 'day');
            return (
              <Paper
                key={key}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.default, 0.4),
                  borderColor: isToday
                    ? alpha(theme.palette.primary.main, 0.5)
                    : theme.palette.divider,
                  minHeight: 180,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: isToday ? 'primary.main' : 'text.secondary',
                        fontWeight: 700,
                      }}
                    >
                      {day.format('ddd')}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      sx={{ lineHeight: 1.1, color: isToday ? 'primary.main' : 'text.primary' }}
                    >
                      {day.format('MMM D')}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={dayTasks.length}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.dark,
                    }}
                  />
                </Stack>

                {dayTasks.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No tasks
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {dayTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onToggleComplete={handleToggleComplete}
                        onEdit={handleEditTask}
                        onDelete={(t) => deleteTask(t.id)}
                        onViewCommunication={handleViewCommunication}
                        onOpenContact={handleOpenContact}
                        compact
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : range === 'month' ? (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 0.75, sm: 1 },
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            }}
          >
            {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const).map((label) => (
              <Typography
                key={label}
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textAlign: 'center',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 0.5,
                }}
              >
                {label}
              </Typography>
            ))}
            {monthGridDays.map((day) => {
              const key = day.format('YYYY-MM-DD');
              const dayTasks = tasksByDayKey.get(key) || [];
              const isToday = day.isSame(dayjs(), 'day');
              const inMonth = day.isSame(anchorDate, 'month');
              return (
                <Paper
                  key={key}
                  variant="outlined"
                  sx={{
                    p: { xs: 0.75, sm: 1 },
                    borderRadius: 2,
                    bgcolor: inMonth
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.action.hover, 0.06),
                    borderColor: isToday
                      ? alpha(theme.palette.primary.main, 0.5)
                      : theme.palette.divider,
                    minHeight: { xs: 100, sm: 120, md: 140 },
                    opacity: inMonth ? 1 : 0.55,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.75 }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      sx={{
                        lineHeight: 1,
                        color: isToday ? 'primary.main' : inMonth ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {day.format('D')}
                    </Typography>
                    {inMonth && (
                      <Chip
                        size="small"
                        label={dayTasks.length}
                        sx={{
                          height: 20,
                          minWidth: 22,
                          fontWeight: 700,
                          '& .MuiChip-label': { px: 0.5 },
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.dark,
                        }}
                      />
                    )}
                  </Stack>
                  {inMonth && dayTasks.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      —
                    </Typography>
                  ) : inMonth ? (
                    <Stack spacing={0.75}>
                      {dayTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onToggleComplete={handleToggleComplete}
                          onEdit={handleEditTask}
                          onDelete={(t) => deleteTask(t.id)}
                          onViewCommunication={handleViewCommunication}
                          onOpenContact={handleOpenContact}
                          compact
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Paper>
              );
            })}
          </Box>
        </Box>
      ) : visibleTasks.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.background.default, 0.6),
          }}
        >
          <Typography variant="h6" gutterBottom>
            Nothing scheduled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No tasks today
            {contactFilterId ? ' for the selected contact' : ''}. Create one to get started.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            disabled={formContacts.length === 0}
          >
            New task
          </Button>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {groupedTasks.map(([dayKey, dayTasks]) => (
            <Box key={dayKey}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={dayjs(dayKey).format('dddd, MMM D')}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.dark,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {dayTasks.length} task{dayTasks.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                {dayTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                    onDelete={(t) => deleteTask(t.id)}
                    onViewCommunication={handleViewCommunication}
                    onOpenContact={handleOpenContact}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <TaskFormDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        customers={formContacts}
        defaultCustomerId={contactFilterId || undefined}
        isSubmitting={isCreating}
        initialTask={editingTask}
        assignableUsers={hasFullAccess ? assignableUsers : undefined}
      />

      <CompleteTaskCommunicationDialog
        open={Boolean(communicationTask)}
        task={communicationTask}
        onClose={() => setCommunicationTask(null)}
        onSent={handleCommunicationSent}
        sendEmail={handleSendTaskEmail}
        sendText={handleSendTaskText}
      />

      <ViewSentCommunicationDialog
        open={Boolean(viewingCommunicationTask)}
        task={viewingCommunicationTask}
        onClose={() => setViewingCommunicationTask(null)}
      />

      <ContactFormDialog
        open={contactFormOpen}
        onClose={() => {
          setContactFormOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact}
      />
    </Box>
  );
};

export default TasksTab;
